import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, QueryList, WritableSignal, computed, effect, inject, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { Design } from 'src/app/interfaces/design';
import { MatDialog } from '@angular/material/dialog';
import { UnavailableDialogComponent } from '../unavailable.dialog/unavailable.dialog.component';
import { DesignsService } from 'src/app/services/designs/designs.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { elementAt, Subscription } from 'rxjs';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { DesignCategory } from 'src/app/interfaces/design-category';
import { CheckoutService } from '../../services/checkout.service';
import { DisplayableDesignCategory } from 'src/app/interfaces/displayable-design-category';
import { Navigation2Service } from '../../services/navigation2.service';
import { ScrollService } from 'src/app/services/scroll/scroll.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-design',
  templateUrl: './design.component.html',
  styleUrls: ['./design.component.scss']
})
export class DesignComponent {
  private readonly scrollService = inject(ScrollService);
  private navigationService = inject(Navigation2Service);
  private checkoutService = inject(CheckoutService);
  private rulesService = inject(RulesService);
  private loadingService = inject(LoadingService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private designsService = inject(DesignsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private dialog = inject(MatDialog);
  private designCards = viewChildren('DesignCard', { read: ElementRef<HTMLDivElement> });
  private changeDetector = inject(ChangeDetectorRef);
  private container = viewChild('Container', { read: ElementRef<HTMLDivElement> });

  // @Input() designs: Design[] = [];
  @Input() shouldAnimateIn = false;
  @Input() shouldAnimateOut = false;
  shouldAnimateSearchIn = false;
  shouldAnimateSearchOut = false;
  designs = computed(() => this.designsService.values());
  selectedDesign = computed(() => this.selectedAttributeService.getValue().design);
  shouldShowSearch = true;
  shouldScroll = true;
  // availableDesigns = computed(() => this.selectedAttributeService.availableAttributes().design);
  availableDesigns = signal<number[]>([]);
  reachableDesigns = signal<number[]>([]);
  filteredDesigns = signal<Design[]>([]);
  designsToShow = computed(() => {
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const reachableDesigns = this.reachableDesigns();
    const filteredDesigns = this.filteredDesigns();
    const designs = this.designs();
    const designsToUse = filteredDesigns.length > 0 ? filteredDesigns : designs;
    if (!isShowingUnavailable) {
      const selectedDesignCode = this.selectedDesign()?.shortName;
      return designsToUse.filter(d => reachableDesigns.includes(d.id) || d.shortName === selectedDesignCode);
    }

    return designsToUse;
  });
  filteredCategories = signal<DesignCategory[]>([]);
  categories = computed(() => this.designsService.categories());
  categoriesToShow = computed(() => {
      const designsToShow = this.designsToShow();
      const filteredCategories = this.filteredCategories();
      const categoriesToShow = (filteredCategories.length > 0 ? filteredCategories : this.categories()).filter(c => designsToShow.some(d => d.categories.includes(c.id)));
      return categoriesToShow;
  });
  expandedCategories = signal(new Map<number, boolean>(), { equal: () => false });
  displayableCategories = computed(() => {
    const result: DisplayableDesignCategory[] = this.categoriesToShow().map(c => Object.assign({}, c, { isExpanded: this.expandedCategories().has(c.id) }));
    return result;
  });
  private undoRedoChangeSubscription: Subscription | null = null;
  isLoadingSub: Subscription | null = null;

  constructor() {
    effect(async () => {
      this.loadingService.load('DesignComponent::GetReachable');
      const reachableAttributes = await this.rulesService.getReachableDesigns(this.selectedAttributeService.configuredItem(), untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
      this.reachableDesigns.set(reachableAttributes);
      this.loadingService.done('DesignComponent::GetReachable');
    }, { allowSignalWrites: true });

    effect(async () => {
      this.loadingService.load('DesignComponent::GetAvailable');
      const availableAttributes = await this.selectedAttributeService.availableAttributes();
      this.availableDesigns.set(availableAttributes.designIDs);
      this.loadingService.done('DesignComponent::GetAvailable');
    }, { allowSignalWrites: true });

    effect(() => {
      this.navigationService.subtitle.set(this.selectedDesign()?.displayName ?? null);
    }, { allowSignalWrites: true });

    this.isLoadingSub = this.loadingService.isLoading.subscribe((isLoading) => {
      const container = this.container();
      this.changeDetector.detectChanges();
      if (!isLoading && container && this.shouldScroll) {
        if (!this.scrollService.didDesignScrollInitial) {
          const selectedCard = this.designCards().find(c => c.nativeElement.firstChild.classList.contains('Selected'));
          if (selectedCard) {
            const containerEl = container.nativeElement;
            const cardEl = selectedCard.nativeElement;
            const containerRect = containerEl.getBoundingClientRect();
            const cardRect = cardEl.getBoundingClientRect();
            containerEl.scrollTop += cardRect.top - containerRect.top - (containerEl.clientHeight / 2) + (cardRect.height / 2);
          }
          this.scrollService.didDesignScrollInitial = true;
        } else {
          this.changeDetector.detectChanges();
          this.scrollService.scrollToPosition('design', container.nativeElement);
        }
        this.shouldScroll = false
      }
    })
  }

  ngOnDestroy() {
    this.undoRedoChangeSubscription?.unsubscribe();
    const container = this.container();
    if (container) {
      this.scrollService.savePosition('design', container.nativeElement.scrollTop);
    }

    this.isLoadingSub?.unsubscribe();
  }

  async designClick(design: Design | null) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'attribute_panel', 'design', design?.shortName);
    }

    if (design) {
      if (this.availableDesigns().includes(design.id)) {
        if (this.selectedDesign()?.displayName !== design?.displayName) {
          this.loadingService.load('DesignComponent::MutateDesign');
          await this.selectedAttributeService.mutateDesign(design);
          this.loadingService.done('DesignComponent::MutateDesign');
        }
      } else {
        try {
          this.loadingService.load('DesignComponent::SetClosestDesign');
          await this.selectedAttributeService.setClosestByDesign(design);
        } catch (e) {
          this.dialog.open(UnavailableDialogComponent, { maxWidth: 450, data: 'design' });
        } finally {
          this.loadingService.done('DesignComponent::SetClosestDesign');
        }
      }
    }
  }

  ngOnInit() {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('view', 'attribute_panel', 'section_view', 'design');
    }
  }

  toggleSearch(force?: boolean) {
    this.shouldShowSearch = force ?? !this.shouldShowSearch;
  }

  searchClick() {
    this.toggleSearch();
  }

  designsFiltered(designs: Design[]) {
    this.filteredDesigns.set(designs);
    // this.designsToShow = designs
    // this.groupedDesignsToShow = this.buildDesignGroups();
  }

  categoriesFiltered(categories: DesignCategory[]) {
    this.filteredCategories.set(categories);
  }

  // buildDesignGroups() {
  //   const groups = this.designsToShow().reduce((obj, design) => {
  //     if (design.category !== obj.previous?.category) {
  //       obj.groups.push([]);
  //     }
  //     obj.groups[obj.groups.length - 1].push(design);
  //     obj.previous = design;
  //     return obj;
  //   }, {groups: [] as Design[][], previous: null as Design | null}).groups;
  //   return groups;
  // }

  getDesignsInCategory(id: number) {
    return this.designsToShow().filter(d => d.categories.includes(id));
  }

  toggleCategoryIsExpanded(category: DisplayableDesignCategory) {
    this.expandedCategories.update(m => {
      if (category.isExpanded) {
        m.delete(category.id);
      } else {
        m.set(category.id, true);
      }
      return m;
    });
  }

  doesContainSelectedDesign(designs: Design[]) {
    return designs.some(d => d.id === this.selectedDesign()?.id);
  }
}
